import React, { useEffect, useState, useMemo, Suspense, lazy } from "react";
import "./Welcome.css";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { setLocalData } from "../../Services/localstoragedata";
import { apiOrderInfo } from "../QrMenuFunctions";
import Spinner from "../Spinner/Spinner";

// Lazy-loaded components
const OrderProgressBar = lazy(() => import("../OrderProgressBar/OrderProgressBar"));
const Footer = lazy(() => import("../Footer/Footer"));
const BillTraceModal = lazy(() => import("../BillTraceModal/BillTraceModal"));

// Skeleton loader
const SkeletonLoader = () => (
  <div className="p-3 text-center">
    <div className="placeholder-glow"><span className="placeholder col-8"></span></div>
    <div className="placeholder-glow mt-2"><span className="placeholder col-6"></span></div>
    <div className="placeholder-glow mt-2"><span className="placeholder col-10"></span></div>
  </div>
);

// LocalStorage cache helpers
const getCachedData = (key, expiryMs = 5 * 60 * 1000) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key));
    if (!cached) return null;
    if (Date.now() - cached.timestamp > expiryMs) return null;
    return cached.data; // ✅ return only the actual data
  } catch {
    return null;
  }
};

const setCachedData = (key, data) => {
  localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
};

const Welcome = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [outletData, setOutletData] = useState([]);
  const [isOutletOpen, setIsOutletOpen] = useState(false);
  const [orderInfo, setOrderInfo] = useState([]);

  const loginDetails = JSON.parse(localStorage.getItem("loginDetails")) || {};
  const custId = loginDetails.customerId || null;
  const custName = loginDetails.customerName || null;

  const [searchParams] = useSearchParams();
  const outletQrCodeParam = searchParams.get("outletCode");
  const rateApply = searchParams.get("rateName");
  const guestHouseId = searchParams.get("guestHouseId");
  const TableNo = searchParams.get("TableNo");

  const API_BASE_URL = process.env.REACT_APP_API_KEY;
  const orderId = localStorage.getItem("orderId") || 0;

  const [currentOutletQrCode, setCurrentOutletQrCode] = useState(
    localStorage.getItem("outletQrCodeData") || null
  );

  // Save URL params to localStorage once
  useEffect(() => {
    if (outletQrCodeParam) {
      localStorage.setItem("outletQrCodeData", outletQrCodeParam);
      setCurrentOutletQrCode(outletQrCodeParam);
    }
    if (rateApply) localStorage.setItem("rateApply", rateApply);
    if (guestHouseId) localStorage.setItem("guestHouseId", guestHouseId);
    if (TableNo) localStorage.setItem("QRTableNo", TableNo);
    localStorage.removeItem("UpdateAndAddAddressId");
  }, [outletQrCodeParam, rateApply, guestHouseId, TableNo]);

  // Fetch menu data
  const fetchMenuData = async (idOutlet, forceRefresh = false) => {
    if (!idOutlet) return;
    const menuKey = `menuData_${idOutlet}`;
    const menuCached = getCachedData(menuKey);

    if (menuCached && !forceRefresh) {
      setLocalData("mainCategories", menuCached.mainCategories);
      setLocalData("cataTypes", menuCached.cataTypes);
      setLocalData("subCata", menuCached.subCata);
      setLocalData("itemMaster", menuCached.itemMaster);
      return;
    }

    try {
      const [mainCategoriesRes, cataTypesRes, subCataRes, itemMasterRes] =
        await Promise.allSettled([
          axios.get(`${API_BASE_URL}MainCata/GetMainCataByOutletId?OutletId=${idOutlet}&food=true`),
          axios.get(`${API_BASE_URL}CategoryType/GetCataTypeByOutletId?OutletId=${idOutlet}`),
          axios.get(`${API_BASE_URL}SubCategory/GetSubCataByOutletId?OutletId=${idOutlet}&food=true`),
          axios.get(`${API_BASE_URL}ItemMaster/GetItemsByRate?OutletId=${idOutlet}&rateApply=${localStorage.getItem("rateApply")}&food=true`),
        ]);

      const menuData = {};

      if (mainCategoriesRes.status === "fulfilled") {
        setLocalData("mainCategories", mainCategoriesRes.value.data);
        menuData.mainCategories = mainCategoriesRes.value.data;
      }
      if (cataTypesRes.status === "fulfilled") {
        setLocalData("cataTypes", cataTypesRes.value.data);
        menuData.cataTypes = cataTypesRes.value.data;
      }
      if (subCataRes.status === "fulfilled") {
        setLocalData("subCata", subCataRes.value.data);
        menuData.subCata = subCataRes.value.data;
      }
      if (itemMasterRes.status === "fulfilled") {
        const filteredItems = itemMasterRes.value.data.filter(item => item.rateApply !== 0);
        setLocalData("itemMaster", filteredItems);
        menuData.itemMaster = filteredItems;
      }

      setCachedData(menuKey, menuData);
    } catch (err) {
      console.error("Error fetching menu data:", err);
    }
  };

  // Fetch outlet data
  const fetchOutletData = async (outletQr, forceRefresh = false) => {
    try {
      setIsLoading(true);
      const cachedOutlet = getCachedData("outletdataLocal");

      if (cachedOutlet && !forceRefresh && cachedOutlet[0]?.outletQrCode === outletQr) {
        setOutletData(cachedOutlet);
        await fetchMenuData(cachedOutlet[0]?.outletId, forceRefresh);
        return;
      }

      const res = await axios.get(`${API_BASE_URL}QrMenu/QrMenuByOutletQrCode?outletQrCode=${outletQr}`);
      const fetchedOutlet = res.data || [];

      // ✅ always enrich data before caching (fix missing regionalLanguage/isOpen issue)
      const enriched = fetchedOutlet.map(out => ({
        ...out,
        isOpen: calculateIsOpen(out.openTime, out.closeTime)
      }));

      setOutletData(enriched);
      setCachedData("outletdataLocal", enriched);

      await fetchMenuData(enriched[0]?.outletId, forceRefresh);
    } catch (err) {
      console.error("Error fetching outlet data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to calculate open/close status
  const calculateIsOpen = (openTime, closeTime) => {
    if (!openTime || !closeTime) return false;

    const now = new Date();
    const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    const [openH, openM = 0] = openTime.split(".").map(Number);
    const [closeH, closeM = 0] = closeTime.split(".").map(Number);

    const openSeconds = openH * 3600 + openM * 60;
    const closeSeconds = closeH * 3600 + closeM * 60;

    return openSeconds < closeSeconds
      ? currentSeconds >= openSeconds && currentSeconds <= closeSeconds
      : currentSeconds >= openSeconds || currentSeconds <= closeSeconds;
  };

  // Load outlet data when QR changes
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const forceRefresh = !!outletQrCodeParam;
      if (currentOutletQrCode) await fetchOutletData(currentOutletQrCode, forceRefresh);
      setIsLoading(false);
    };
    loadData();
  }, [currentOutletQrCode, outletQrCodeParam]);

  // Keep open/close status updated
  useEffect(() => {
    if (!outletData.length) return;
    const status = calculateIsOpen(outletData[0].openTime, outletData[0].closeTime);
    setIsOutletOpen(status);

    // also update cached outlet
    const cached = getCachedData("outletdataLocal");
    if (cached?.length) {
      cached[0].isOpen = status;
      setCachedData("outletdataLocal", cached);
    }
  }, [outletData]);

  // Order info auto-refresh
  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = () => apiOrderInfo(setOrderInfo);
    fetchOrder();
    const interval = setInterval(fetchOrder, 60000);
    return () => clearInterval(interval);
  }, [orderId]);

  // Compute current order status
  const currentStatus = useMemo(() => {
    const statusMap = { 1: "Placed", 2: "Accepted", 3: "Ready", 4: "Pick Up", 5: "Delivered" };
    const status = orderInfo?.onlineOrderStatusDetails?.[0]?.orderStatus;
    return statusMap[status] || "Unknown Status";
  }, [orderInfo]);

  if (isLoading) return <Spinner />;

  return (
    <div className="container-fluid">
      <div className="col-md-12">
        <div className="row">
          {/* Navbar */}
          <nav className="navbar navbar-light bg-light shadow-sm bg-body rounded">
            <div className="container-fluid">
              {outletData.map(about => (
                <span key={about.outletId} className="navbar-brand">
                  <img
                    src={about.outletlogo}
                    alt={about.outletName}
                    width={60}
                    height={40}
                    className="me-2"
                    loading="lazy"
                  />
                  <strong>{about.outletName}</strong>
                </span>
              ))}
            </div>
          </nav>

          {/* Carousel */}
          <div className="mt-2">
            {outletData[0]?.photo?.length ? (
              <div id="carouselExampleFade" className="carousel slide carousel-fade" data-bs-ride="carousel">
                <div className="carousel-inner">
                  {outletData[0].photo.map((photo, index) => (
                    <div key={photo.photoId || index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
                     <img
                      src={photo.photoUrl}
                      className="d-block w-100"
                      loading="lazy"
                      style={{ height: "250px" }}
                      alt={photo.photoName}
                    />
                    </div>
                  ))}
                </div>
                <button className="carousel-control-prev" type="button" data-bs-target="#carouselExampleFade" data-bs-slide="prev">
                  <span className="carousel-control-prev-icon" aria-hidden="true" />
                  <span className="visually-hidden">Previous</span>
                </button>
                <button className="carousel-control-next" type="button" data-bs-target="#carouselExampleFade" data-bs-slide="next">
                  <span className="carousel-control-next-icon" aria-hidden="true" />
                  <span className="visually-hidden">Next</span>
                </button>
              </div>
            ) : <p className="text-muted">No images available</p>}
          </div>

          {/* About & Status */}
          <div className="mt-4">
            <div className="d-flex align-items-center">
              <h6>
                {custId ? (
                  <>Welcome... <span style={{ color: "orange" }}><i>{custName}</i></span></>
                ) : (
                  <>Visit Us For .......</>
                )}
              </h6>
              {!isOutletOpen && <div className="text-danger ms-3">We are Closed Now</div>}
            </div>
            <div style={{ marginLeft: "10px" }}>
              {outletData.map(about => (
                <div key={about.outletId}>
                  {about.aboutUsPara1} <br />
                  {about.aboutUsPara2} <br />
                  {about.aboutUsPara3}
                </div>
              ))}
            </div>
          </div>

          {/* Order Info */}
          {orderId > 0 && (
            <div className="p-0 mt-2">
              <div className="d-flex p-0">
                <strong className="me-2" style={{ marginLeft: "20px" }}>OrderId:</strong>
                <div className="me-4">{orderId}</div>
                {orderInfo.extraTime != null && (
                  <div className="text-danger">{`Sorry, We are late by ${orderInfo.extraTime} Minutes`}</div>
                )}
              </div>
              <Suspense fallback={<SkeletonLoader />}>
                <OrderProgressBar orderStatus={currentStatus} />
              </Suspense>
            </div>
          )}

          {/* Bottom Options */}
          <div className="fixed-bottom bg-white shadow-sm">
            <div className="AllOptionsImages">
              <Link to="/QrMenuPage"><button className="btn btn-sm btn-outline-primary">Menu</button></Link>
              <Link to="/Feedback"><button className="btn btn-sm btn-outline-success">Feedback</button></Link>
              <BillTraceModal outletData={outletData} />
              <Link to="/AboutUs"><button className="btn btn-sm btn-outline-info">About Us</button></Link>
            </div>
            <Suspense fallback={<SkeletonLoader />}><Footer /></Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
